//---------------------------------------------------------
// Nodes table on the pair detail (UX lock #51). Role is the BIRD role the job
// wrote; never LoxiLB MASTER/BACKUP. Addresses and ids are mono.
//---------------------------------------------------------
import {Box, Button} from '@mui/material';
import {LINODE_PAUSED_TOOLTIP, useLinodePaused} from 'hooks/useLinodeMutate';
import {SoftIconAction, SoftTable, SoftColumn} from 'components/soft/SoftUI';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PowerIcon from '@mui/icons-material/Power';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import type {HANodeIndex, HAPairNode} from 'types/haPair';
import {HoldsVipChip} from './HAChips';
import {linodeIdCell, nodeHealthLabel, nodePowerButtons, retryLabel, roleLabel, syncLabel, NODE_COLUMNS} from './haCopy';

export default function HANodesTable(props: {
	nodes: HAPairNode[];
	holder: HANodeIndex | null;
	saving: boolean;
	canPower: boolean;
	canRetry: boolean;
	onPower: (node: HANodeIndex, action: 'shutdown' | 'boot' | 'reboot') => void;
	onRetry: (node: HANodeIndex) => void;
}) {
	const {nodes, holder, saving, canPower, canRetry, onPower, onRetry} = props;
	const paused = useLinodePaused();
	const tip = paused ? LINODE_PAUSED_TOOLTIP : undefined;
	const rows = [...nodes].sort((a, b) => a.node - b.node);
	const cols: SoftColumn<HAPairNode>[] = [
		{key: 'node', header: NODE_COLUMNS[0], render: n => `Node ${n.node}`},
		{key: 'role', header: NODE_COLUMNS[1], render: n => <span data-testid={`role-${n.node}`}>{roleLabel(n.role)}</span>},
		{key: 'vip', header: NODE_COLUMNS[2], render: n => (holder === n.node ? <HoldsVipChip /> : null)},
		{key: 'health', header: NODE_COLUMNS[3], render: n => <span data-testid={`health-${n.node}`}>{nodeHealthLabel(n.health)}</span>},
		{key: 'pub', header: NODE_COLUMNS[4], mono: true, render: n => n.publicIp || '—'},
		{key: 'priv', header: NODE_COLUMNS[5], mono: true, render: n => n.privateIp || '—'},
		{key: 'linode', header: NODE_COLUMNS[6], mono: true, render: n => linodeIdCell(n)},
		{
			key: 'sync',
			header: NODE_COLUMNS[7],
			render: n => {
				const state = saving ? 'saving' : n.syncState;
				return (
					<Box display="flex" alignItems="center" gap="8px">
						<span data-testid={`sync-${n.node}`}>{syncLabel(state)}</span>
						{!saving && n.syncState === 'out_of_sync' && canRetry && (
							<Button size="small" variant="outlined" onClick={() => onRetry(n.node)} data-testid={`row-retry-${n.node}`}>
								{retryLabel(n.node)}
							</Button>
						)}
					</Box>
				);
			},
		},
		{
			key: 'power',
			header: NODE_COLUMNS[8],
			align: 'right',
			render: n => {
				if (!canPower) return null;
				const b = nodePowerButtons(n.linodeStatus);
				return (
					<Box display="flex" justifyContent="flex-end" data-testid={`power-${n.node}`}>
						{b.power === 'off' && <SoftIconAction label={`Power off node ${n.node}`} icon={<PowerSettingsNewIcon />} destructive onClick={() => onPower(n.node, 'shutdown')} disabled={paused} tooltip={tip} />}
						{b.power === 'on' && <SoftIconAction label={`Power on node ${n.node}`} icon={<PowerIcon />} onClick={() => onPower(n.node, 'boot')} disabled={paused} tooltip={tip} />}
						{b.reboot && <SoftIconAction label={`Reboot node ${n.node}`} icon={<RestartAltIcon />} onClick={() => onPower(n.node, 'reboot')} disabled={paused} tooltip={tip} />}
					</Box>
				);
			},
		},
	];
	return <SoftTable label="Nodes" columns={cols} rows={rows} rowKey={n => String(n.node)} empty="No nodes" />;
}
